import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
  ReactNode,
} from 'react';

interface RecentPage {
  path: string;
  label: string;
  timestamp: number;
}

interface RecentPagesContextType {
  recentPages: RecentPage[];
  addPage: (path: string, label: string) => void;
  clearHistory: () => void;
}

const RecentPagesContext = createContext<RecentPagesContextType | undefined>(undefined);

const MAX_RECENT_PAGES = 5;
const STORAGE_KEY = 'recent-pages';

// Route labels for display
const routeLabels: Record<string, string> = {
  '/': 'Dashboard',
  '/categories': 'Categories',
  '/products': 'Products',
  '/menu': 'Menu',
  '/materials': 'Materials',
  '/warehouses': 'Warehouses',
  '/assets': 'Assets',
  '/manufacturing': 'Manufacturing',
  '/recipes': 'Recipes',
  '/orders': 'Orders',
  '/analytics': 'Analytics',
  '/customers': 'Customers',
  '/users': 'Users',
  '/action-logs': 'Action Logs',
  '/settings': 'Settings',
};

type IdleTaskHandle = ReturnType<typeof setTimeout> | number;

function schedulePersist(pages: RecentPage[]): IdleTaskHandle | null {
  if (typeof requestIdleCallback !== 'undefined') {
    return requestIdleCallback(() => {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(pages));
    });
  } else {
    // Fallback for browsers that don't support requestIdleCallback
    return setTimeout(() => {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(pages));
    }, 0);
  }
}

export function RecentPagesProvider({ children }: { children: ReactNode }) {
  const [recentPages, setRecentPages] = useState<RecentPage[]>(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch (error) {
        console.error('Failed to parse recent pages from localStorage', error);
        return [];
      }
    }
    return [];
  });

  const persistTimeoutRef = useRef<IdleTaskHandle | null>(null);

  // Cancel any pending persist operation when pages change
  const cancelPersist = useCallback(() => {
    if (persistTimeoutRef.current !== null) {
      if (typeof requestIdleCallback !== 'undefined') {
        cancelIdleCallback(persistTimeoutRef.current as number);
      } else {
        clearTimeout(persistTimeoutRef.current);
      }
      persistTimeoutRef.current = null;
    }
  }, []);

  // Persist to localStorage whenever recentPages change
  useEffect(() => {
    cancelPersist();
    persistTimeoutRef.current = schedulePersist(recentPages);

    return cancelPersist;
  }, [recentPages, cancelPersist]);

  const addPage = useCallback((path: string, label: string) => {
    setRecentPages(prev => {
      // Remove the page if it already exists
      const filtered = prev.filter(page => page.path !== path);

      // Add the new page at the beginning
      const newPage: RecentPage = {
        path,
        label: label || routeLabels[path] || path,
        timestamp: Date.now(),
      };

      const updated = [newPage, ...filtered];

      // Keep only the most recent MAX_RECENT_PAGES
      return updated.slice(0, MAX_RECENT_PAGES);
    });
  }, []);

  const clearHistory = useCallback(() => {
    setRecentPages([]);
  }, []);

  // Auto-track page navigation
  useEffect(() => {
    // Only track pages if we're in a browser environment with router context
    try {
      // Check if we're in a browser and have access to window.location
      if (typeof window !== 'undefined' && window.location) {
        const path = window.location.pathname;
        const label = routeLabels[path];

        if (label) {
          addPage(path, label);
        }
      }
    } catch (error) {
      // Silently ignore errors when not in browser or router context
      // This can happen during SSR or when context is not available
    }
  }, [addPage]);

  const contextValue = useMemo(() => ({
    recentPages,
    addPage,
    clearHistory,
  }), [recentPages, addPage, clearHistory]);

  return (
    <RecentPagesContext.Provider value={contextValue}>
      {children}
    </RecentPagesContext.Provider>
  );
}

export function useRecentPages() {
  const context = useContext(RecentPagesContext);
  if (context === undefined) {
    throw new Error('useRecentPages must be used within a RecentPagesProvider');
  }
  return context;
}